import React, { useEffect, useState } from 'react';
import { useLandlordStore } from '@/stores/useLandlordStore';
import { ArrowLeft, Moon, ChevronDown, CheckCircle, Wrench, XCircle, Clock, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const MaintenanceList: React.FC = () => {
    const { maintenanceRequests, fetchMaintenanceRequests, updateMaintenanceStatus, loading } = useLandlordStore();
    const navigate = useNavigate();

    // Track which dropdown is open (by request ID)
    const [openDropdown, setOpenDropdown] = useState<string | null>(null);
    const [updatingId, setUpdatingId] = useState<string | null>(null);

    useEffect(() => {
        fetchMaintenanceRequests();
    }, []);

    const handleUpdateStatus = async (requestId: string, newStatus: string) => {
        setOpenDropdown(null);
        setUpdatingId(requestId);
        try {
            await updateMaintenanceStatus(requestId, newStatus);
        } catch (error) {
            console.error(error);
        } finally {
            setUpdatingId(null);
        }
    };

    const getStatusStyle = (status: string) => {
        switch (status) {
            case "Completed":
                return {
                    text: "text-green-600 dark:text-green-400",
                    bg: "bg-green-100 dark:bg-green-900/30",
                    icon: CheckCircle,
                };
            case "In Progress":
                return {
                    text: "text-blue-600 dark:text-blue-400",
                    bg: "bg-blue-100 dark:bg-blue-900/30",
                    icon: Wrench,
                };
            case "Cancelled":
                return {
                    text: "text-red-600 dark:text-red-400",
                    bg: "bg-red-100 dark:bg-red-900/30",
                    icon: XCircle,
                };
            case "Pending":
            default:
                return {
                    text: "text-light-subtle dark:text-dark-subtle",
                    bg: "bg-gray-200 dark:bg-gray-800",
                    icon: Clock,
                };
        }
    };

    if (loading && maintenanceRequests.length === 0) {
        return (
            <div className="flex flex-col flex-1 items-center justify-center p-12 min-h-[60vh]">
                <Loader2 className="animate-spin text-primary dark:text-golden w-10 h-10 mb-4" />
            </div>
        );
    }

    return (
        <div className="space-y-6 pb-12 min-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 border-b border-light-border dark:border-dark-border">
                <button
                    onClick={() => navigate(-1)}
                    className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
                >
                    <ArrowLeft size={24} className="text-primary dark:text-golden" />
                </button>
                <h1 className="text-xl font-bold text-light-text dark:text-dark-text">Maintenance Queue</h1>
            </div>

            {maintenanceRequests.length === 0 ? (
                <div className="flex flex-col flex-1 items-center justify-center p-12 text-center mt-20 text-light-subtle dark:text-dark-subtle">
                    <Moon size={60} className="mb-4 opacity-70" />
                    <p className="text-lg">No maintenance requests found.</p>
                </div>
            ) : (
                <div className="px-4 md:px-6 space-y-4">
                    {maintenanceRequests.map((item) => {
                        const statusStyle = getStatusStyle(item.status);
                        const isDropdownOpen = openDropdown === item.id;
                        const isUpdating = updatingId === item.id;

                        return (
                            <div
                                key={item.id}
                                className="bg-light-card dark:bg-dark-card rounded-[12px] border border-light-border dark:border-dark-border shadow-sm flex flex-col relative"
                            >
                                {/* Card Header */}
                                <div className="flex justify-between items-center p-4">
                                    <div className="flex items-center gap-3">
                                        {/* Avatar (assuming missing from web response, defaulting to dicebear/placehold) */}
                                        <img
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(item.tenant_name)}&background=random`}
                                            alt={item.tenant_name}
                                            className="w-10 h-10 rounded-full"
                                        />
                                        <div className="flex flex-col">
                                            <span className="text-base font-bold text-light-text dark:text-dark-text">
                                                {item.tenant_name}
                                            </span>
                                            <span className="text-[13px] text-light-subtle dark:text-dark-subtle">
                                                {item.property_name} - {item.unit_number}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Status Badge Dropdown Toggle */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setOpenDropdown(isDropdownOpen ? null : item.id)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${statusStyle.bg}`}
                                            disabled={isUpdating}
                                        >
                                            {isUpdating ? (
                                                <Loader2 size={16} className={`animate-spin ${statusStyle.text}`} />
                                            ) : (
                                                <>
                                                    <span className={`text-xs font-bold ${statusStyle.text}`}>
                                                        {item.status}
                                                    </span>
                                                    <ChevronDown size={14} className={statusStyle.text} />
                                                </>
                                            )}
                                        </button>

                                        {/* Dropdown Menu */}
                                        {isDropdownOpen && !isUpdating && (
                                            <div className="absolute right-0 top-full mt-2 w-40 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-light-border dark:border-dark-border z-10 overflow-hidden">
                                                {['Pending', 'In Progress', 'Completed'].map(opt => (
                                                    <button
                                                        key={opt}
                                                        onClick={() => handleUpdateStatus(item.id, opt)}
                                                        className="w-full text-left px-4 py-3 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors border-b border-light-border dark:border-dark-border last:border-b-0 text-light-text dark:text-dark-text"
                                                    >
                                                        {opt}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => handleUpdateStatus(item.id, 'Cancelled')}
                                                    className="w-full text-left px-4 py-3 text-sm font-bold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Card Body */}
                                <div className="px-4 pb-3 pt-3 border-t border-light-border dark:border-dark-border/50 flex flex-col gap-1">
                                    <span className="text-[15px] font-semibold text-light-text dark:text-dark-text">
                                        {item.title || 'Maintenance Request'}
                                    </span>
                                    <span className="text-sm leading-relaxed text-light-subtle dark:text-dark-subtle">
                                        {item.description}
                                    </span>
                                </div>

                                {/* Date Footer */}
                                <span className="text-xs text-right px-4 pb-4 text-light-subtle dark:text-dark-subtle">
                                    Reported on {new Date(item.created_at).toLocaleDateString()}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Backdrop to close dropdowns */}
            {openDropdown && (
                <div
                    className="fixed inset-0 z-0"
                    onClick={() => setOpenDropdown(null)}
                />
            )}
        </div>
    );
};
